import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders, HttpParams} from "@angular/common/http";
import {EMPTY, Observable, of, throwError} from "rxjs";
import {edsEnvConfiguration} from "../../environments/edsEnvConfiguration";
import {IeDsFolder, IUserInfo} from "../models/user-info";
import {catchError, map, mergeMap, tap, timeout} from "rxjs/operators";
import {XmlUtils} from "../common/xmlUtils";
import jwtDecode, {JwtPayload} from "jwt-decode";
import {ActionsService} from "../Redux/Actions/actions.service";
import {ApplicationUserInfoActions} from "../Redux/Actions/application-user-info-actions";

interface TokenCognito {
  access_token: string; // JWToken;
  expires_in: number;
  id_token: string; // JWToken;
  refresh_token: string; // JWToken;
  token_type: 'Bearer';
}

interface IdentityPayload {
  userId: string;
  providerName: string;
  providerType: string;
  issuer: string;
  primary: string;
  dateCreated: string;
}
type TokenPayload = JwtPayload & {identities: IdentityPayload[], given_name: string, locale: string, nickname: string, family_name: string};

const edsCognitoTokenKey = 'edsCognitoToken';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  constructor(private http: HttpClient, private act: ActionsService) { }

  private set token(value: TokenCognito|null ) {
    if (value) {
      localStorage.setItem(edsCognitoTokenKey, JSON.stringify(value));
    } else {
      localStorage.removeItem(edsCognitoTokenKey);
    }
  }
  private get token(): TokenCognito|null {
    const _tokenStr = localStorage.getItem(edsCognitoTokenKey);
    return _tokenStr ? JSON.parse(_tokenStr) : null;
  }

  private get user(): IUserInfo|null {
    const _t = this.token;
    if ( _t ) {
      const decode = jwtDecode<TokenPayload>(_t.id_token);
      return {
        who: decode.given_name ? decode.given_name : decode['cognito:username'], // decode['cognito:username'] for auth in DEV with stromboli.auth
        mail: decode.identities && decode.identities[0] && decode.identities[0].userId ? decode.identities[0].userId : decode['email'],  // decode['email'] for auth in DEV with stromboli.auth
        locale: decode.locale ? decode.locale : ""
      };
    }
    return null;
  }


  private static getTimeStamp(): string {
    const now = new Date();
    return "A" + now.getFullYear() + now.getMonth() + now.getDay() + now.getHours() + now.getMinutes() + now.getSeconds();
  }

  private getBearerToken() {
    const t = this.token;
    return t ? `Bearer ${t.id_token}` : '';
  }

  private headers() {
    return new HttpHeaders()
      .set('Authorization',  this.getBearerToken())
      .set('Cache-Control', 'no-cache');
  }

  public get(url: string) {
    return this.refreshToken().pipe(
      mergeMap( _ => this.http.get(url, {responseType: 'text', headers: this.headers()}))
    );
  }

  public delete(url: string) {
    return this.refreshToken().pipe(
      mergeMap( _ => this.http.delete(url, {responseType: 'text', headers: this.headers()}))
    );
  }

  // This method is still not tested
  public updateItem(fileId: string, newContent: any) {
    return this.refreshToken().pipe(
      mergeMap( _ => this.http.put(edsEnvConfiguration.userFile + fileId, newContent, {headers: this.headers()}))
    );
  }
  // This method is still not tested type F for file D for folder
  public addItem(parentId: string, name: string, description: string, type: "F" | "D", content: any) {
    const body = {
      file_parent_id: parentId,
      file_description: description,
      file_name: name,
      file_type: type,
      content: content
    };

    return this.refreshToken().pipe(
      mergeMap( _ => this.http.post(edsEnvConfiguration.addItem, body, {headers: this.headers()}))
    );
  }


  public rename(renameUrl: string, newName: string) {
    return this.refreshToken().pipe(
      mergeMap( _ => this.http.put(renameUrl, {new_name: newName}, {headers: this.headers()}))
    );
  }

  public userFiles(): Observable<IeDsFolder> {
    return this.refreshToken().pipe(
      mergeMap( _ =>
              this.get(edsEnvConfiguration.listUserFiles + AuthService.getTimeStamp()).pipe(
                timeout(10000),   // 10s timeout
                XmlUtils.toJSON,
                map(x => x as IeDsFolder),
                // tap(x => console.log('3', x))
            )
      )
    );
  }

  private refreshToken(): Observable<boolean> {
    const t = this.token;
    return t ?
      this.http.post(edsEnvConfiguration.refresh_token(t.refresh_token), new HttpParams(), {responseType: 'json', withCredentials: true}).pipe(
        mergeMap((x: TokenCognito) => {
          this.token = {...x, refresh_token: t.refresh_token};  // the refresh token dosn't have the key refresh token
          return of(true);
        }),
        catchError(err => {
          this.token = null;
          this.act.sendAction(ApplicationUserInfoActions.loginUser());
          return throwError('Error on refresh token'); } )
      )
      : throwError('Error token not available ');
  }

  // old data userInfo
  // public userInfo(): Observable<IUserInfo> {
  //   return this.http.get(edsEnvConfiguration.userLogged + AuthService.getTimeStamp()).pipe(
  //     timeout(10000),   // 10s timeout
  //     XmlUtils.toJSON,
  //     map(x => {
  //       if (Utils.getFieldValue(x, 'item.label', null) === null) {
  //         throw new Error('Invalid data user');
  //       }
  //       return x.item as IUserInfo;
  //     })
  //   );
  // }

  public userInfo(): Observable<IUserInfo> {
    const parameters = new URLSearchParams(window.location.search);
    const code = parameters.get('code');
    if (code) {
      const newurl = window.location.protocol + "//" + window.location.host + window.location.pathname + window.location.hash;
      if (history.pushState) {
        // This need to remove the code param in url maintaining the base url.
        window.history.pushState({path: newurl}, '', newurl);
      }
      const t = edsEnvConfiguration.user_token(code, newurl);
      return this.http.post(t, new HttpParams(), {responseType: 'json', withCredentials: true}).pipe(
        timeout(10000),   // 10s timeout
        tap((x: TokenCognito) => {
            // console.log('TOKEN', x);
            this.token = x;
          }
        ),
        map( x => this.user),
      );
    } else if (this.user) {
       return of(this.user);
    }
    this.token = null;
    return throwError('no code defined');
  }

}
